<template>
  <div ref="raiz" :class="['rf', { 'rf--bloque': bloque, 'rf--contorno': contorno, 'rf--abierto': abierto, 'rf--borrable': vaciable && (desde || hasta) }]">
    <button type="button" class="rf-campo" :disabled="disabled" :aria-expanded="abierto ? 'true' : 'false'" @click="alternar">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <rect x="3" y="4" width="18" height="18" rx="3" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
      </svg>
      <span v-if="desde || hasta" class="rf-txt">{{ textoDelCampo }}</span>
      <span v-else class="rf-txt rf-txt--vacio">{{ $t("rangoFechas.desde") }}<span class="rf-flecha">→</span>{{ $t("rangoFechas.hasta") }}</span>
    </button>

    <button v-if="vaciable && (desde || hasta)" type="button" class="rf-borrar" :aria-label="$t('rangoFechas.quitar')" :title="$t('rangoFechas.quitar')" @click="vaciar">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
    </button>

    <div v-if="abierto" ref="pop" v-al-body class="rf-pop" :style="posicion" role="dialog" :aria-label="$t('rangoFechas.titulo')">
      <p class="rf-ayuda">{{ !a ? $t("rangoFechas.elegirInicio") : !b ? $t("rangoFechas.elegirFin") : `${corta(a)} → ${corta(b)} · ${dias(a, b)} ${$tc("rangoFechas.dias", dias(a, b))}` }}</p>
      <div class="rf-meses">
        <div v-for="(m, i) in meses" :key="m.clave" class="rf-mes">
          <div class="rf-mes-cab">
            <button v-if="i === 0" type="button" class="rf-nav" :aria-label="$t('rangoFechas.anterior')" @click="mover(-1)">‹</button>
            <span v-else class="rf-nav-hueco"></span>
            <span class="rf-mes-tit">{{ m.titulo }}</span>
            <button v-if="i === meses.length - 1" type="button" class="rf-nav" :aria-label="$t('rangoFechas.siguiente')" @click="mover(1)">›</button>
            <span v-else class="rf-nav-hueco"></span>
          </div>
          <div class="rf-sem"><span v-for="l in letras" :key="l">{{ l }}</span></div>
          <div class="rf-dias" @mouseleave="sobre = ''">
            <span v-for="n in m.hueco" :key="'h' + n" class="rf-vacio"></span>
            <button
              v-for="d in m.dias"
              :key="d"
              type="button"
              :class="clasesDelDia(d)"
              :disabled="bloqueado(d)"
              @click="tocar(d)"
              @mouseenter="sobre = d"
            ><span class="rf-dia__n">{{ Number(d.slice(8)) }}</span></button>
          </div>
        </div>
      </div>
      <div class="rf-pie">
        <button v-if="vaciable && (desde || hasta)" type="button" class="rf-btn rf-btn--quitar" @click="vaciar">{{ $t("rangoFechas.quitar") }}</button>
        <button type="button" class="rf-btn rf-btn--ghost" @click="abierto = false">{{ $t("rangoFechas.cancelar") }}</button>
        <button type="button" class="rf-btn rf-btn--ok" :disabled="!a || (!b && !parcial)" @click="aplicar">{{ $t("rangoFechas.aplicar") }}</button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Rango de fechas en un solo calendario: se toca el primer día y luego el último. No deja
 * un «hasta» anterior al «desde» (tocar antes del inicio empieza el rango ahí). Emite
 * `change` con { from, to } en formato AAAA-MM-DD.
 *
 * El calendario se pinta en <body>: dentro de un cajón o un modal con scroll se cortaba.
 *  - min / max: días que se pueden tocar.
 *  - minDias: distancia mínima entre los dos días (1 en una reserva: salir otro día).
 *  - vaciable: deja quitar las fechas (filtros); emite { from: "", to: "" }.
 *  - parcial: deja aplicar solo el primer día (validez sin fin); emite { from, to: "" }.
 *  - bloque: el campo ocupa todo el ancho, para formularios.
 */
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const deYmd = (s) => {
  const [y, m, d] = String(s || "").split("-").map(Number);
  return y ? new Date(y, m - 1, d) : new Date();
};

export default {
  name: "RangoDeFechas",
  props: {
    desde: { type: String, default: "" },
    hasta: { type: String, default: "" },
    min: { type: String, default: "" },
    max: { type: String, default: "" },
    minDias: { type: Number, default: 0 },
    vaciable: { type: Boolean, default: false },
    parcial: { type: Boolean, default: false },
    bloque: { type: Boolean, default: false },
    contorno: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  directives: {
    alBody: {
      inserted(el) {
        document.body.appendChild(el);
      },
      unbind(el) {
        if (el.parentNode) el.parentNode.removeChild(el);
      },
    },
  },
  data() {
    const ini = deYmd(this.desde);
    return { abierto: false, a: this.desde, b: this.hasta, base: new Date(ini.getFullYear(), ini.getMonth(), 1), hoy: ymd(new Date()), posicion: {}, cuantos: 2, sobre: "" };
  },
  computed: {
    /** «01 oct – 30 oct 2026»: el año una sola vez si no cambia, para que quepa en una barra. */
    textoDelCampo() {
      const { desde, hasta } = this;
      if (!desde || !hasta) return this.corta(desde || hasta);
      if (desde.slice(0, 10) === hasta.slice(0, 10)) return this.corta(desde);
      // Cruza de año: los dos años, cortos, para que quepa en el mismo ancho.
      if (desde.slice(0, 4) !== hasta.slice(0, 4)) {
        const conAno = (s) => deYmd(s).toLocaleDateString(this.locale, { day: "2-digit", month: "short", year: "2-digit" });
        return `${conAno(desde)} – ${conAno(hasta)}`;
      }
      const sinAno = deYmd(desde).toLocaleDateString(this.locale, { day: "2-digit", month: "short" });
      return `${sinAno} – ${this.corta(hasta)}`;
    },
    locale() {
      return (this.$i18n && this.$i18n.locale) || "es";
    },
    letras() {
      // Lunes primero, en el idioma del panel.
      const lunes = new Date(2024, 0, 1);
      return Array.from({ length: 7 }, (_, i) => new Date(2024, 0, 1 + i).toLocaleDateString(this.locale, { weekday: "narrow" })).map((l, i) => l || String(i + lunes.getDay()));
    },
    meses() {
      return Array.from({ length: this.cuantos }, (_, k) => k).map((k) => {
        const p = new Date(this.base.getFullYear(), this.base.getMonth() + k, 1);
        const total = new Date(p.getFullYear(), p.getMonth() + 1, 0).getDate();
        const t = p.toLocaleDateString(this.locale, { month: "long", year: "numeric" });
        return {
          clave: ymd(p),
          titulo: t.charAt(0).toUpperCase() + t.slice(1),
          hueco: (p.getDay() + 6) % 7,
          dias: Array.from({ length: total }, (_, i) => ymd(new Date(p.getFullYear(), p.getMonth(), i + 1))),
        };
      });
    },
  },
  mounted() {
    document.addEventListener("mousedown", this.fuera);
    window.addEventListener("resize", this.colocar);
    window.addEventListener("scroll", this.colocar, true);
  },
  beforeDestroy() {
    document.removeEventListener("mousedown", this.fuera);
    window.removeEventListener("resize", this.colocar);
    window.removeEventListener("scroll", this.colocar, true);
  },
  methods: {
    corta(s) {
      return s ? deYmd(s).toLocaleDateString(this.locale, { day: "2-digit", month: "short", year: "numeric" }).replace(/\./g, "") : "—";
    },
    dias(x, y) {
      return Math.round((deYmd(y) - deYmd(x)) / 86400000) + 1;
    },
    alternar() {
      if (!this.abierto) {
        this.a = String(this.desde || "").slice(0, 10);
        this.b = String(this.hasta || "").slice(0, 10);
        const ini = deYmd(this.a || this.min);
        this.base = new Date(ini.getFullYear(), ini.getMonth(), 1);
        this.cuantos = window.innerWidth < 640 ? 1 : 2;
      }
      this.abierto = !this.abierto;
      if (this.abierto) {
        this.colocar();
        this.$nextTick(this.colocar);
      }
    },
    /** Debajo del campo; arriba si no cabe, y siempre dentro de la pantalla. */
    colocar() {
      if (!this.abierto || !this.$refs.raiz) return;
      const campo = this.$refs.raiz.getBoundingClientRect();
      const pop = this.$refs.pop;
      const ancho = pop ? pop.offsetWidth : this.cuantos * 252 + (this.cuantos - 1) * 22 + 36;
      const alto = pop ? pop.offsetHeight : 420;
      const margen = 12;
      const left = Math.max(margen, Math.min(campo.left, window.innerWidth - ancho - margen));
      let top = campo.bottom + 8;
      if (top + alto > window.innerHeight - margen) {
        top = campo.top - alto - 8 >= margen ? campo.top - alto - 8 : Math.max(margen, window.innerHeight - alto - margen);
      }
      this.posicion = { top: `${top}px`, left: `${left}px` };
    },
    bloqueado(d) {
      return Boolean((this.min && d < this.min) || (this.max && d > this.max));
    },
    /** El final del rango: el elegido o, mientras se elige, el día bajo el ratón. */
    clasesDelDia(d) {
      const fin = this.b || (this.a && this.sobre > this.a ? this.sobre : "");
      return [
        "rf-dia",
        {
          "rf-dia--ini": d === this.a,
          "rf-dia--fin": d === this.b,
          "rf-dia--con-banda": d === this.a && fin > this.a,
          "rf-dia--banda-fin": d === fin && fin > this.a,
          "rf-dia--dentro": this.a && fin && d > this.a && d < fin,
          "rf-dia--hoy": d === this.hoy,
        },
      ];
    },
    mover(n) {
      this.base = new Date(this.base.getFullYear(), this.base.getMonth() + n, 1);
    },
    tocar(d) {
      if (!this.a || this.b || d < this.a || this.dias(this.a, d) - 1 < this.minDias) {
        this.a = d;
        this.b = "";
      } else {
        this.b = d;
      }
    },
    aplicar() {
      if (!this.a || (!this.b && !this.parcial)) return;
      this.$emit("change", { from: this.a, to: this.b });
      this.abierto = false;
    },
    vaciar() {
      this.$emit("change", { from: "", to: "" });
      this.abierto = false;
    },
    fuera(e) {
      if (!this.abierto || !this.$refs.raiz) return;
      if (this.$refs.raiz.contains(e.target) || (this.$refs.pop && this.$refs.pop.contains(e.target))) return;
      this.abierto = false;
    },
  },
};
</script>

<style scoped>
/* Por defecto, igual que los campos de las barras de filtros: gris relleno, sin borde. */
/* Ancho fijo: elegir fechas no mueve nada de la barra. Cabe cualquier rango. */
.rf { position: relative; display: inline-block; width: 230px; max-width: 100%; min-width: 0; font-family: "DM Sans", sans-serif; }
.rf-campo {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  max-width: 100%;
  height: 38px;
  padding: 0 14px;
  border: 0;
  border-radius: 10px;
  background: #f1f5f9;
  color: #374151;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s ease, box-shadow 0.2s ease;
}
.rf-campo:hover { background: #e9eef5; }
.rf-campo:focus-visible, .rf--abierto .rf-campo { outline: none; background: #f1f5f9; box-shadow: 0 0 0 3px rgba(103, 61, 230, 0.14); }
.rf-campo:disabled { opacity: 0.6; cursor: default; }
.rf-campo svg { flex: none; width: 16px; height: 16px; color: #6b7280; }
/* Interlineado propio: con el 1 del campo, el recorte se comía la «g» y la «p». */
.rf-txt { overflow: hidden; text-overflow: ellipsis; line-height: 1.5; }
.rf-txt--vacio { color: #6b7280; font-weight: 400; }
.rf-txt--vacio .rf-flecha { margin: 0 7px; color: #9ca3af; }
.rf--borrable .rf-campo { padding-right: 34px; }
.rf-borrar {
  position: absolute;
  top: 50%;
  right: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  transform: translateY(-50%);
}
.rf-borrar:hover { background: #dfe5ee; color: #111; }
.rf-borrar svg { width: 13px; height: 13px; }

/* En formularios: a todo el ancho. */
.rf--bloque { display: block; width: 100%; }
.rf--bloque .rf-campo { height: 44px; border-radius: 12px; background: #f2f2f5; font-size: 14px; font-weight: 500; color: #111; }
.rf--bloque .rf-campo:hover { background: #eaeaef; }

/* En barras de campos blancos, como Analíticas. */
.rf--contorno .rf-campo { border: 1px solid #e5e7eb; background: #fff; color: #111; font-size: 13px; }
.rf--contorno .rf-campo:hover { background: #fff; border-color: #673de6; }
.rf--contorno.rf--bloque .rf-campo { height: 46px; border-radius: 11px; font-size: 14.5px; font-weight: 400; color: #0f172a; }
.rf--contorno .rf-campo:focus-visible, .rf--contorno.rf--abierto .rf-campo { background: #fff; border-color: #673de6; }

/* El calendario */
.rf-pop {
  position: fixed;
  z-index: 100000;
  max-height: calc(100vh - 24px);
  overflow-y: auto;
  padding: 18px 18px 16px;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0px 0px 24px 4px rgba(17, 17, 17, 0.1);
  font-family: "DM Sans", sans-serif;
  text-align: left;
}
.rf-ayuda { margin: 0 0 14px; font-size: 13px; font-weight: 600; color: #6b7280; }
.rf-meses { display: flex; gap: 24px; }
.rf-mes { width: 252px; }
.rf-mes-cab { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.rf-mes-tit { font-size: 14px; font-weight: 700; color: #111; }
.rf-nav { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; padding: 0 0 2px; border: 0; border-radius: 50%; background: #f2f2f5; font-size: 18px; line-height: 1; cursor: pointer; color: #111; transition: background 0.15s ease, color 0.15s ease; }
.rf-nav:hover { background: #111; color: #fff; }
.rf-nav-hueco { width: 30px; }
.rf-sem, .rf-dias { display: grid; grid-template-columns: repeat(7, 36px); }
.rf-sem span { padding-bottom: 6px; text-align: center; font-size: 11px; font-weight: 600; color: #9ca3af; text-transform: uppercase; }
.rf-dias { row-gap: 2px; }

/* Cada día ocupa toda su celda para que la banda del rango sea continua; la píldora va dentro. */
.rf-dia { display: flex; align-items: center; justify-content: center; height: 34px; padding: 0; border: 0; background: transparent; font: inherit; cursor: pointer; }
.rf-dia__n { display: flex; align-items: center; justify-content: center; width: 34px; height: 34px; border-radius: 10px; font-size: 13px; font-weight: 600; color: #111; transition: background 0.12s ease; }
.rf-dia:hover:not(:disabled) .rf-dia__n { background: #f0ebfd; }
.rf-dia--dentro { background: #f0ebfd; }
.rf-dia--con-banda { background: linear-gradient(90deg, transparent 50%, #f0ebfd 50%); }
.rf-dia--banda-fin { background: linear-gradient(90deg, #f0ebfd 50%, transparent 50%); }
.rf-dia--hoy .rf-dia__n { box-shadow: inset 0 0 0 1.5px #673de6; }
.rf-dia--ini .rf-dia__n, .rf-dia--fin .rf-dia__n,
.rf-dia--ini:hover:not(:disabled) .rf-dia__n, .rf-dia--fin:hover:not(:disabled) .rf-dia__n { background: #111; color: #fff; box-shadow: none; }
.rf-dia:disabled { cursor: default; }
.rf-dia:disabled .rf-dia__n { color: #d1d5db; }

.rf-pie { display: flex; align-items: center; justify-content: flex-end; gap: 8px; margin-top: 14px; padding-top: 14px; border-top: 1px solid #f1f1f4; }
.rf-btn { height: 38px; padding: 0 18px; border-radius: 12px; font: inherit; font-size: 13.5px; font-weight: 600; cursor: pointer; transition: background 0.15s ease; }
.rf-btn--ghost { border: 0; background: #f2f2f5; color: #111; }
.rf-btn--ghost:hover { background: #e9e9ee; }
.rf-btn--ok { border: 0; background: #673de6; color: #fff; }
.rf-btn--ok:hover:not(:disabled) { background: #5b32d0; }
.rf-btn--ok:disabled { opacity: 0.45; cursor: default; }
.rf-btn--quitar { margin-right: auto; padding-left: 4px; border: 0; background: transparent; color: #6b7280; }
.rf-btn--quitar:hover { color: #111; }
@media (max-width: 640px) {
  .rf-meses { flex-direction: column; }
}
</style>
